import { noop } from "lodash-es";
import type { ConnectionOptions, NatsConnection, Status } from "nats.ws";
import { connect, DebugEvents, ErrorCode, Events, NatsError } from "nats.ws";

import { ApplicationError } from "@/error/ApplicationError";
import { CancelError } from "@/error/CancelError";
import { reload } from "@/error/reload";
import { captureException, captureMessage } from "@/error/sentry";
import type { LogoutReason } from "@/router/types";
import { useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";
import { SharedResource } from "@/utils/async/SharedResource";
import { isBrowserTabActive } from "@/utils/dom/dom";
import { natsUrl } from "@/utils/env/Environment";
import { isNonPrimitive } from "@/utils/record";
import type { UnknownRecord } from "@/utils/type";

import { refreshToken } from "./http/api.config";
import { lastError } from "./natsInternal";
import { closeSession } from "./read/BackendReader";

const connectRetries = 1;
const connResource = new SharedResource<Connection>();

export type Connection = NatsConnection & { onError: (msg: string, info?: UnknownRecord) => void };

interface CloseHandler {
  login: () => void | Promise<unknown>;
  logout: (reason: LogoutReason) => void | Promise<unknown>;
}

export async function openConnection(
  currentPath: () => string,
  onClose: CloseHandler,
  connectionName = "",
): Promise<Connection> {
  return connResource.acquire(() => doOpenConnection(currentPath, onClose, connectionName));
}

async function doOpenConnection(
  currentPath: () => string,
  onClose: CloseHandler,
  connectionName: string,
): Promise<Connection> {
  const connection = await doConnect(
    {
      servers: [natsUrl],
      pingInterval: 4500,
      maxPingOut: 1,
      maxReconnectAttempts: -1,
      name: connectionName,
      waitOnFirstConnect: true,
    },
    doRefreshToken,
  );
  void addCloseConnectionHandlers(connection, doRefreshToken, onClose.logout);
  if (connection.isClosed()) {
    throw new CancelError("Connection closed while it was created");
  }
  void addConnectionStateHandlers(connection);
  return connection;

  async function doRefreshToken() {
    try {
      await refreshToken({ path: "#" + currentPath() });
      reload("refresh-token");
    } catch (_) {
      void onClose.logout("refresh-failed");
    }
  }
}

async function doConnect(
  options: ConnectionOptions,
  refreshToken: () => Promise<unknown>,
  retries = 0,
) {
  const start = Date.now();
  try {
    return Object.assign(await connect(options), { onError: noop });
  } catch (error) {
    if (shouldRefreshToken(error)) {
      await refreshToken();
      throw new CancelError("Token refresh in progress");
    }
    if (shouldReload(error)) {
      if (retries < connectRetries) {
        return doConnect(options, refreshToken, retries + 1);
      }
      captureException(error, { request: { timeout: Date.now() - start } });
      // TODO show a message somehow?
      throw error;
    }
    if (logoutReason(error)) {
      // TODO show a message somehow?
      throw error;
    }
    captureException(error);
    throw error;
  }
}

export async function closeConnection() {
  const current = connResource.value();
  // release before closing so that a failing close() cannot leave it behind
  const released = connResource.release();
  // closed synchronously, so that it cannot tear down a session that belongs to a later connection
  closeSession(current !== undefined && !current.isClosed());
  try {
    // awaited so that a connection that is still being created is closed too
    const connection = await released;
    if (connection && !connection.isClosed()) {
      await connection.close();
    }
  } catch (e) {
    captureException(e);
  }
}

async function addConnectionStateHandlers(connection: Connection) {
  for await (const status of connection.status()) {
    try {
      handleStatus(connection, status);
    } catch (e) {
      statusError(e);
    }
  }
}

function handleStatus(connection: Connection, status: Status) {
  if (status.type !== DebugEvents.PingTimer) {
    useConnectionStore().addWebsocketState(status.type);
  }
  switch (status.type) {
    case Events.Disconnect:
    case DebugEvents.StaleConnection:
      useConnectionStore().disconnectWebsocket();
      break;
    case Events.Reconnect:
      useStatisticsStore().current.reconnects++;
      useConnectionStore().reconnectWebsocket();
      break;
    case Events.Error: {
      // prefer status.data over lastError
      // lastError can be cleared or overwritten before the queued status is consumed
      switch (status.data) {
        case ErrorCode.AuthorizationViolation:
        case ErrorCode.AuthenticationExpired:
        case ErrorCode.AccountExpired:
          // these trigger a connection.close and then a token refresh/page reload, or logout. don't log
          return;
        case ErrorCode.PermissionsViolation:
          // lastError is never populated for permission errors
          connection.onError(String(status.data), status.permissionContext);
          return;
      }
      const message = lastError(connection)?.message;
      switch (message) {
        case NATS_ERRORS.AUTH_REVOKED:
          // reported as ErrorCode.ProtocolError, only the server text identifies it. don't log
          break;
        case NATS_ERRORS.STALE_CONNECTION:
          // browser freeze causes server ping timeout as browser tab is completely off
          // just unfrozen -> ok, don't log
          if (isBrowserTabActive() && !(useConnectionStore().timeSinceLastUnfreeze! < 2000)) {
            connection.onError("Server-side ping timeout");
          }
          break;
        case NATS_ERRORS.AUTH_TIMEOUT:
          // Needs more investigation... keep logging for now
          if (isBrowserTabActive()) {
            connection.onError(message);
          }
          break;
        default:
          connection.onError(
            message ?? (isNonPrimitive(status.data) ? "unknown" : String(status.data)),
          );
      }
      break;
    }
  }
}

function statusError(e: unknown) {
  try {
    captureException(e);
  } catch (_) {
    // nothing more we can do
  }
}

async function addCloseConnectionHandlers(
  connection: Connection,
  refreshToken: () => Promise<unknown>,
  logout: (reason: LogoutReason) => void | Promise<unknown>,
) {
  const clientId = connection.info?.client_id;
  const error = await connection.closed();
  if (connResource.value() === connection) {
    await closeConnection();
  }
  if (!error) {
    return;
  }
  if (shouldRefreshToken(error)) {
    await refreshToken();
    return;
  }
  if (shouldReload(error)) {
    return reload("connection-closed");
  }
  const reason = logoutReason(error);
  if (reason) {
    void logout(reason);
    return;
  }
  captureMessage(`Unhandled connection error: ${error.message}`, {
    error: { clientId, info: connection.info, error },
  });
  if (error instanceof NatsError && error.chainedError) {
    captureException(error.chainedError);
  }
  void logout("unknown");
}

function shouldRefreshToken(error: unknown) {
  if (error instanceof NatsError) {
    const refreshErrors = [
      ErrorCode.AuthenticationExpired,
      ErrorCode.AuthorizationViolation,
      ErrorCode.Disconnect,
    ].map((err) => err.valueOf());
    if (refreshErrors.includes(error.code)) {
      return true;
    }

    const refreshServerErrors = [NATS_ERRORS.AUTH_REVOKED];
    if (error.isProtocolError() && refreshServerErrors.includes(error.message)) {
      return true;
    }
  }
}

function shouldReload(error: unknown) {
  if (error instanceof NatsError) {
    const reloadServerErrors = [NATS_ERRORS.AUTH_TIMEOUT, NATS_ERRORS.STALE_CONNECTION];
    if (error.isProtocolError() && reloadServerErrors.includes(error.message)) {
      return true;
    }
  }
  if (unexpectedServerMessage(error)?.startsWith("-ERR 'Authentication Timeout'")) {
    return true;
  }
}

function logoutReason(error: unknown): LogoutReason | undefined {
  if (error instanceof NatsError) {
    switch (error.code) {
      case ErrorCode.ConnectionRefused.valueOf():
        return "connection-refused";
      case ErrorCode.AccountExpired.valueOf():
        return "account-expired";
    }
  }
}

function unexpectedServerMessage(error: unknown): string | undefined {
  if (error instanceof ApplicationError) {
    if (error.message === "unexpected response from server") {
      return error.contexts.response?.data?.toString();
    }
  }
}

const NATS_ERRORS = {
  AUTH_REVOKED: "'User Authentication Revoked'",
  AUTH_TIMEOUT: "'Authentication Timeout'",
  STALE_CONNECTION: "'Stale Connection'",
};
